import React, { useEffect, useRef, useState } from 'react';
import { BoxSelect, Car, ChevronDown, Compass, Download, DoorOpen, FileText, Frame, ImagePlus, Magnet, MousePointer2, Ruler, Sofa, Square } from 'lucide-react';
import { FLOOR_PLAN_PDF_PAPER, type FloorPlanPdfPaper } from './floorPlanPdf';
import {
  FLOOR_PLAN_CATALOG,
  FLOOR_PLAN_CATALOG_GROUPS,
  type FloorPlanCatalogGroup,
} from './floorPlanCatalog';
import type { FloorPlanThickness, FloorPlanTool } from './floorPlanTypes';

function StairsIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 21h6v-4h4v-4h4V9h4V3" />
    </svg>
  );
}

interface FloorPlanToolbarProps {
  tool: FloorPlanTool;
  thickness: FloorPlanThickness;
  placeCatalogId: string | null;
  catalogGroup: FloorPlanCatalogGroup;
  title: string;
  widthDraft: string;
  depthDraft: string;
  onToolChange: (tool: FloorPlanTool) => void;
  onThicknessChange: (thickness: FloorPlanThickness) => void;
  onCatalogGroupChange: (group: FloorPlanCatalogGroup) => void;
  onPlaceCatalog: (catalogId: string) => void;
  onTitleChange: (title: string) => void;
  onWidthChange: (value: string) => void;
  onDepthChange: (value: string) => void;
  onApplyFootprint: () => void;
  onAddTrace?: () => void;
  onAddImages?: (files: File[]) => void;
  importing?: boolean;
  snapEnabled?: boolean;
  onToggleSnap?: () => void;
  onExportPdf?: (paper: FloorPlanPdfPaper) => void;
  exportingPdf?: boolean;
  onCancel: () => void;
  onDone: () => void;
}

type MenuId = 'draw' | 'thickness' | 'furniture' | 'stairs' | 'size' | 'pdf' | null;

const DRAW_TOOLS: { id: FloorPlanTool; label: string; icon: React.ReactNode }[] = [
  { id: 'select', label: 'Select', icon: <MousePointer2 size={16} /> },
  { id: 'wall', label: 'Wall', icon: <Frame size={16} /> },
  { id: 'rect', label: 'Rectangle', icon: <BoxSelect size={16} /> },
  { id: 'door', label: 'Door', icon: <DoorOpen size={16} /> },
  { id: 'window', label: 'Window', icon: <Square size={16} /> },
  { id: 'garage_door', label: 'Garage door', icon: <Car size={16} /> },
];

function drawLabel(tool: FloorPlanTool): string {
  if (tool === 'place' || tool === 'select' || tool === 'dimension') return 'Draw';
  return DRAW_TOOLS.find((item) => item.id === tool)?.label || 'Draw';
}

const FloorPlanToolbar: React.FC<FloorPlanToolbarProps> = ({
  tool,
  thickness,
  placeCatalogId,
  catalogGroup,
  title,
  widthDraft,
  depthDraft,
  onToolChange,
  onThicknessChange,
  onCatalogGroupChange,
  onPlaceCatalog,
  onTitleChange,
  onWidthChange,
  onDepthChange,
  onApplyFootprint,
  onAddTrace,
  onAddImages,
  importing,
  snapEnabled = true,
  onToggleSnap,
  onExportPdf,
  exportingPdf,
  onCancel,
  onDone,
}) => {
  const [menu, setMenu] = useState<MenuId>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const items = FLOOR_PLAN_CATALOG.filter((item) => item.group === catalogGroup);
  const stairItems = FLOOR_PLAN_CATALOG.filter((item) => item.group === 'stairs');
  const placed = FLOOR_PLAN_CATALOG.find((item) => item.id === placeCatalogId);
  const placingStairs = tool === 'place' && placed?.group === 'stairs';
  const placingFurniture = tool === 'place' && placed && placed.group !== 'marks' && placed.group !== 'stairs';

  useEffect(() => {
    if (!menu) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenu(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menu]);

  const toggle = (id: MenuId) => setMenu((cur) => (cur === id ? null : id));

  return (
    <div className="board-toolbar-dock fp-toolbar-dock">
    <div className="board-toolbar fp-toolbar" ref={wrapRef}>
      <button
        type="button"
        className={`board-tool${tool === 'select' ? ' is-active' : ''}`}
        title="Select"
        onClick={() => {
          onToolChange('select');
          setMenu(null);
        }}
      >
        <MousePointer2 size={16} />
        <span className="board-tool-label">Select</span>
      </button>

      <button
        type="button"
        className={`board-tool${tool === 'dimension' ? ' is-active' : ''}`}
        title="Set a distance from another point"
        aria-label="Dimension"
        onClick={() => {
          onToolChange('dimension');
          setMenu(null);
        }}
      >
        <Ruler size={16} />
        <span className="board-tool-label">Dim</span>
      </button>

      <div className="fp-menu-wrap">
        <button
          type="button"
          className={`board-tool${menu === 'draw' || (tool !== 'select' && tool !== 'place' && tool !== 'dimension') ? ' is-active' : ''}${menu === 'draw' ? ' is-open' : ''}`}
          aria-label="Draw"
          onClick={() => toggle('draw')}
        >
          <Frame size={16} />
          <span className="board-tool-label">{drawLabel(tool)}</span>
          <ChevronDown size={14} className="board-tools-chevron" />
        </button>
        {menu === 'draw' && (
          <div className="board-tools-dropdown fp-menu" role="menu">
            {DRAW_TOOLS.filter((item) => item.id !== 'select').map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                className={`board-tools-item${tool === item.id ? ' is-active' : ''}`}
                onClick={() => {
                  onToolChange(item.id);
                  setMenu(null);
                }}
              >
                <span className="board-tools-item-icon">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="fp-menu-wrap">
        <button
          type="button"
          className={`board-tool${menu === 'thickness' ? ' is-open is-active' : ''}`}
          onClick={() => toggle('thickness')}
        >
          <span>Wall {thickness}"</span>
          <ChevronDown size={14} className="board-tools-chevron" />
        </button>
        {menu === 'thickness' && (
          <div className="board-tools-dropdown fp-menu" role="menu">
            {([4, 6, 8] as FloorPlanThickness[]).map((n) => (
              <button
                key={n}
                type="button"
                role="menuitem"
                className={`board-tools-item${thickness === n ? ' is-active' : ''}`}
                onClick={() => {
                  onThicknessChange(n);
                  setMenu(null);
                }}
              >
                {n}" walls
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="fp-menu-wrap">
        <button
          type="button"
          className={`board-tool${placingFurniture || menu === 'furniture' ? ' is-active' : ''}${menu === 'furniture' ? ' is-open' : ''}`}
          onClick={() => toggle('furniture')}
        >
          <Sofa size={16} />
          <span className="board-tool-label">{placingFurniture ? placed.label : 'Furniture'}</span>
          <ChevronDown size={14} className="board-tools-chevron" />
        </button>
        {menu === 'furniture' && (
          <div className="board-tools-dropdown fp-menu fp-menu--catalog" role="menu">
            <div className="fp-menu-tabs">
              {FLOOR_PLAN_CATALOG_GROUPS.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  className={`fp-menu-tab${catalogGroup === group.id ? ' is-active' : ''}`}
                  onClick={() => onCatalogGroupChange(group.id)}
                >
                  {group.label}
                </button>
              ))}
            </div>
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                className={`board-tools-item${placeCatalogId === item.id && tool === 'place' ? ' is-active' : ''}`}
                onClick={() => {
                  onPlaceCatalog(item.id);
                  setMenu(null);
                }}
              >
                <span className="fp-menu-item-copy">
                  <span>{item.label}</span>
                  <span className="fp-menu-item-size">
                    {item.widthIn}" × {item.depthIn}"
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        className={`board-tool${tool === 'place' && placeCatalogId === 'north-arrow' ? ' is-active' : ''}`}
        title="Place a north arrow"
        onClick={() => {
          onPlaceCatalog('north-arrow');
          setMenu(null);
        }}
      >
        <Compass size={16} />
        <span className="board-tool-label">North</span>
      </button>

      <div className="fp-menu-wrap">
        <button
          type="button"
          className={`board-tool${placingStairs || menu === 'stairs' ? ' is-active' : ''}${menu === 'stairs' ? ' is-open' : ''}`}
          title="Place stairs"
          aria-label="Stairs"
          onClick={() => toggle('stairs')}
        >
          <StairsIcon size={16} />
          <span className="board-tool-label">{placingStairs ? placed.label : 'Stairs'}</span>
          <ChevronDown size={14} className="board-tools-chevron" />
        </button>
        {menu === 'stairs' && (
          <div className="board-tools-dropdown fp-menu fp-menu--catalog" role="menu">
            {stairItems.map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                className={`board-tools-item${placeCatalogId === item.id && tool === 'place' ? ' is-active' : ''}`}
                onClick={() => {
                  onPlaceCatalog(item.id);
                  setMenu(null);
                }}
              >
                <span className="fp-menu-item-copy">
                  <span>{item.label}</span>
                  <span className="fp-menu-item-size">
                    {item.widthIn}" × {item.depthIn}"
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {onAddImages && (
        <>
          <button
            type="button"
            className="board-tool"
            title="Add a photo or scan to trace"
            disabled={importing}
            onClick={() => {
              setMenu(null);
              imageInputRef.current?.click();
            }}
          >
            <ImagePlus size={16} />
            <span className="board-tool-label">{importing ? 'Adding…' : 'Image'}</span>
          </button>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*,.heic,.heif"
            multiple
            aria-label="Import floor plan image"
            style={{ display: 'none' }}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length) onAddImages(files);
              e.target.value = '';
            }}
          />
        </>
      )}

      {onAddTrace && (
        <button
          type="button"
          className="board-tool"
          title="Add a PDF page or 3D model capture to trace"
          disabled={importing}
          onClick={() => {
            setMenu(null);
            onAddTrace();
          }}
        >
          <FileText size={16} />
          <span className="board-tool-label">{importing ? 'Adding…' : 'Plans / Model'}</span>
        </button>
      )}

      <div className="fp-menu-wrap">
        <button
          type="button"
          className={`board-tool${menu === 'size' ? ' is-open is-active' : ''}`}
          onClick={() => toggle('size')}
        >
          <span>
            {widthDraft || "36'"} × {depthDraft || "24'"}
          </span>
          <ChevronDown size={14} className="board-tools-chevron" />
        </button>
        {menu === 'size' && (
          <div className="board-tools-dropdown fp-menu fp-menu--size" role="menu">
            <label className="fp-field">
              Title
              <input
                id="fp-editor-title"
                value={title}
                placeholder="Floor plan"
                onChange={(e) => onTitleChange(e.target.value)}
              />
            </label>
            <p className="fp-menu-note">Drawing area only — the building is the walls you draw.</p>
            <div className="fp-footprint-fields">
              <label>
                Sheet W
                <input
                  value={widthDraft}
                  onChange={(e) => onWidthChange(e.target.value)}
                  onBlur={onApplyFootprint}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onApplyFootprint();
                  }}
                />
              </label>
              <span className="fp-times">×</span>
              <label>
                Sheet D
                <input
                  value={depthDraft}
                  onChange={(e) => onDepthChange(e.target.value)}
                  onBlur={onApplyFootprint}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onApplyFootprint();
                  }}
                />
              </label>
            </div>
          </div>
        )}
      </div>

      <span className="board-toolbar-sep" />

      {onExportPdf && (
        <div className="fp-menu-wrap">
          <button
            type="button"
            className={`board-tool${menu === 'pdf' ? ' is-open is-active' : ''}`}
            title="Export a printable PDF"
            aria-label="Export PDF"
            disabled={exportingPdf}
            onClick={() => toggle('pdf')}
          >
            <Download size={16} />
            <span className="board-tool-label">{exportingPdf ? 'Exporting…' : 'PDF'}</span>
            <ChevronDown size={14} className="board-tools-chevron" />
          </button>
          {menu === 'pdf' && (
            <div className="board-tools-dropdown fp-menu" role="menu">
              {(['letter', 'tabloid'] as FloorPlanPdfPaper[]).map((paper) => (
                <button
                  key={paper}
                  type="button"
                  role="menuitem"
                  className="board-tools-item"
                  onClick={() => {
                    onExportPdf(paper);
                    setMenu(null);
                  }}
                >
                  {FLOOR_PLAN_PDF_PAPER[paper].label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {onToggleSnap && (
        <button
          type="button"
          className={`board-tool icon-only${snapEnabled ? ' is-active' : ''}`}
          title={snapEnabled ? 'Snapping on — hold Alt to bypass' : 'Snapping off'}
          aria-label="Snapping"
          aria-pressed={snapEnabled}
          onClick={onToggleSnap}
        >
          <Magnet size={16} />
        </button>
      )}

      <button type="button" className="board-tool" onClick={onCancel}>
        Cancel
      </button>
      <button type="button" className="board-tool is-active" onClick={onDone}>
        Done
      </button>
    </div>
    </div>
  );
};

export default FloorPlanToolbar;
